import { updatePOSVendorDetails, removePOSData } from "../pages/login/services";

let inFlightSyncPromise = null;
let lastSyncAttemptTimestamp = 0;
let lastFailedSyncTime = 0;

// Absolute safety limits
const MIN_SYNC_INTERVAL_MS = 60 * 1000; // Never attempt more than once every 60 seconds under ANY circumstance
const FAILED_SYNC_COOLDOWN = 60 * 1000;  // 1 minute cooldown on failure

/**
 * Checks if vendor details need to be refreshed based on sync_interval_minutes.
 * If elapsed time >= sync_interval_minutes, calls GET /vendor-details and updates Redux state.
 *
 * @param {Function} dispatch Redux dispatch function
 * @param {Function} getState Redux getState function
 * @returns {Promise<void>}
 */
export const checkAndSyncVendorDetails = async (dispatch, getState) => {
  try {
    if (!getState || !dispatch) return;
    const state = getState();
    const posData = state?.posData;
    const token = posData?.token;

    // 1. If user is not logged in, no need to sync
    if (!token) return;

    const now = Date.now();

    // 2. Hard safety throttle: Never allow more than 1 call per minute regardless of state
    if (now - lastSyncAttemptTimestamp < MIN_SYNC_INTERVAL_MS) {
      return;
    }

    // 3. Resolve sync interval in minutes (defaults to 15 if invalid or missing)
    const rawInterval = posData?.sync_interval_minutes ?? posData?.vendor?.sync_interval_minutes;
    const parsedInterval = Number(rawInterval);
    const syncIntervalMinutes = !isNaN(parsedInterval) && parsedInterval > 0 ? parsedInterval : 15;
    const syncIntervalMs = syncIntervalMinutes * 60 * 1000;

    const lastFetchedTime = Number(posData?.lastFetchedTime || 0);

    // 4. If data was fetched within the sync interval, do not call API
    if (lastFetchedTime > 0 && (now - lastFetchedTime < syncIntervalMs)) {
      return;
    }

    // 5. Prevent rapid retries if previous call failed
    if (now - lastFailedSyncTime < FAILED_SYNC_COOLDOWN) {
      return;
    }

    // 6. If a request is already in-flight, await it to prevent duplicate simultaneous calls
    if (inFlightSyncPromise) {
      return await inFlightSyncPromise;
    }

    // Mark attempt timestamp immediately to block any concurrent or rapid follow-up triggers
    lastSyncAttemptTimestamp = now;

    inFlightSyncPromise = (async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_ROUTE || "https://serve-link.app/api";
        const response = await fetch(`${baseUrl}/vendor-details`, {
          method: "GET",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json().catch(() => null);

        if (response.ok && (result?.status === 200 || result?.status === true) && result?.data) {
          dispatch(updatePOSVendorDetails(result.data));
        } else if (
          response.status === 401 ||
          response.status === 403 ||
          response.status === 555 ||
          result?.status === 401 ||
          result?.status === 403 ||
          result?.status === 555
        ) {
          dispatch(removePOSData());
        } else {
          lastFailedSyncTime = Date.now();
        }
      } catch (err) {
        lastFailedSyncTime = Date.now();
        console.error("Error syncing vendor details:", err);
      } finally {
        inFlightSyncPromise = null;
      }
    })();

    return await inFlightSyncPromise;
  } catch (error) {
    console.error("Error in checkAndSyncVendorDetails:", error);
  }
};

/**
 * Redux middleware that globally intercepts API calls and checks if vendor details sync is required.
 */
export const vendorSyncMiddleware = (storeAPI) => (next) => (action) => {
  const result = next(action);

  if (action?.type && typeof action.type === "string") {
    const isApiCall =
      action.type.endsWith("/executeQuery/pending") ||
      action.type.endsWith("/executeMutation/pending");

    if (isApiCall) {
      // Do not sync on login/logout or vendor-details endpoints
      const isAuthAction =
        action.type.startsWith("login/") ||
        action.type.includes("admin-login") ||
        action.type.includes("logout") ||
        action.type.includes("getVendorDetails") ||
        action.type.includes("vendor-details");

      if (!isAuthAction) {
        checkAndSyncVendorDetails(storeAPI.dispatch, storeAPI.getState);
      }
    }
  }

  return result;
};
